/*
 * Copyright (c) 2026 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { HttpContextToken } from '@angular/common/http';

/**
 * Path prefixes that must NEVER be replayed through `oidc_return_url`.
 * Anchored prefix-match: `/auth` matches `/auth` and `/auth/...` but NOT `/auth-history`.
 */
export const OIDC_BLOCKED_ROUTES = ['/auth', '/signup', '/accessdenied', '/error'] as const;

/**
 * Single-segment forms walked by `containsBlockedSegment` (covers outlet/aux URL shapes
 * like `/(popup:auth/login-callback)` and matrix-param injection `/x;auth=...`).
 */
export const OIDC_BLOCKED_SEGMENTS = ['auth', 'signup', 'accessdenied', 'error', 'login-callback'] as const;

/**
 * Multi-segment strings checked against the FULLY-DECODED pathname. Defeats `%2F`-bypass
 * shapes like `/auth%2Flogin-callback` where the segment walk sees a single decoded segment
 * `auth/login-callback` not present in OIDC_BLOCKED_SEGMENTS.
 */
export const OIDC_BLOCKED_MULTI_SEGMENTS = [
  'auth/login-callback',
  'auth/login',
  'auth/logout',
] as const;

/**
 * Query params we never replay through `oidc_return_url`. Includes OIDC artefacts plus the
 * legacy `token=` query-param login vector. Compared case-insensitively against keys
 * walked from `URLSearchParams.keys()` and from `UrlSegment.parameters`.
 */
export const OIDC_BLOCKED_QUERY_PARAMS = [
  'code',
  'state',
  'session_state',
  'access_token',
  'id_token',
  'token',
] as const;

/**
 * Set on outgoing requests that must NOT trigger oidcService.login() on 401. Used by
 * LoginCallbackComponent's user-fetch so a server-side rejection of the freshly-issued
 * token does not re-arm the loop via the 401 interceptor.
 */
export const SKIP_OIDC_RELOGIN_ON_401 = new HttpContextToken<boolean>(() => false);

/**
 * Allowed `?error=` codes surfaced to the login page. The login template must render
 * the translated message keyed off this enum, never the raw query value.
 */
export const LOGIN_ERROR_CODES = ['callback_failed', 'loop_detected', 'session_ended'] as const;
export type LoginErrorCode = typeof LOGIN_ERROR_CODES[number];
